import { BaseService } from '@/chassis/data/services/base-service';
import { BaseFormService, ValidationRules } from '@/chassis/data/services/base-form-service';
import { AuditMetadata, extractEditableFields, resolveCreatedBy, resolveUpdatedBy } from '@/chassis/data/services/form-metadata-utils';
import { createSimpleNormalizer } from '@/chassis/data/services/normalizer-utils';

export interface UserCredentialRecord extends AuditMetadata {
  id: string;
  userId: string;
  cpf: string;
  email: string;
  active?: boolean;
  passwordHash?: string;
  passwordSalt?: string;
  lastPasswordChange?: string;
  passwordExpiresAt?: string;
  requiresPasswordChange?: boolean;
  socialProvider?: string;
  socialProviderId?: string;
  recoveryEmail?: string;
  recoveryPhone?: string;
  securityQuestions?: string[];
  failedLoginAttempts?: number;
  lastFailedLoginAt?: string;
  createdAt?: string;
  updatedAt?: string;
  failedAttempts?: number;
  isLocked?: boolean;
  passwordLastChanged?: string;
}

export interface UserCredentialFilters {
  searchTerm?: string;
  status?: 'active' | 'inactive';
  isLocked?: boolean | '';
  page?: number;
  limit?: number;
}

export interface CreateUserCredentialDto {
  userId: string;
  cpf: string;
  email: string;
  active?: boolean;
  passwordHash?: string;
  passwordSalt?: string;
  lastPasswordChange?: string;
  passwordExpiresAt?: string;
  requiresPasswordChange?: boolean;
  socialProvider?: string;
  socialProviderId?: string;
  recoveryEmail?: string;
  recoveryPhone?: string;
  securityQuestions?: string[];
  failedLoginAttempts?: number;
  lastFailedLoginAt?: string;
}

export type UpdateUserCredentialDto = Partial<CreateUserCredentialDto>;

export type UserCredentialFormState = CreateUserCredentialDto & AuditMetadata;

const baseNormalizeUserCredential = createSimpleNormalizer<UserCredentialRecord>();
const normalizeUserCredential = (record: any): UserCredentialRecord => {
  const normalized = baseNormalizeUserCredential(record);
  const resolvedActive =
    (typeof normalized.active === 'boolean' ? normalized.active : undefined) ??
    (typeof (normalized as any).status === 'string' ? (normalized as any).status !== 'inactive' : undefined) ??
    true;

  normalized.active = resolvedActive;
  return normalized;
};

const sanitizePayload = (form: Partial<UserCredentialFormState>): Partial<CreateUserCredentialDto> => {
  const data = extractEditableFields<CreateUserCredentialDto>(form);
  const sanitizeRequired = (value?: string) => {
    if (typeof value !== 'string') {
      return value;
    }
    return value.trim();
  };

  const sanitizeOptional = (value?: string) => {
    if (typeof value !== 'string') {
      return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  };

  const payload: Partial<CreateUserCredentialDto> = {};

  if (data.userId !== undefined) {
    payload.userId = sanitizeRequired(data.userId);
  }

  if (data.cpf !== undefined) {
    payload.cpf = sanitizeRequired(data.cpf);
  }

  if (data.email !== undefined) {
    payload.email = sanitizeRequired(data.email);
  }

  if (data.passwordHash !== undefined) {
    const value = sanitizeOptional(data.passwordHash);
    if (value !== undefined) {
      payload.passwordHash = value;
    }
  }

  if (data.passwordSalt !== undefined) {
    const value = sanitizeOptional(data.passwordSalt);
    if (value !== undefined) {
      payload.passwordSalt = value;
    }
  }

  if (data.lastPasswordChange !== undefined) {
    const value = sanitizeOptional(data.lastPasswordChange);
    if (value !== undefined) {
      payload.lastPasswordChange = value;
    }
  }

  if (data.passwordExpiresAt !== undefined) {
    const value = sanitizeOptional(data.passwordExpiresAt);
    if (value !== undefined) {
      payload.passwordExpiresAt = value;
    }
  }

  if (data.requiresPasswordChange !== undefined) {
    payload.requiresPasswordChange = data.requiresPasswordChange === true;
  }

  if (data.socialProvider !== undefined) {
    const value = sanitizeOptional(data.socialProvider);
    if (value !== undefined) {
      payload.socialProvider = value;
    }
  }

  if (data.socialProviderId !== undefined) {
    const value = sanitizeOptional(data.socialProviderId);
    if (value !== undefined) {
      payload.socialProviderId = value;
    }
  }

  if (data.recoveryEmail !== undefined) {
    const value = sanitizeOptional(data.recoveryEmail);
    if (value !== undefined) {
      payload.recoveryEmail = value;
    }
  }

  if (data.recoveryPhone !== undefined) {
    const value = sanitizeOptional(data.recoveryPhone);
    if (value !== undefined) {
      payload.recoveryPhone = value;
    }
  }

  if (data.securityQuestions !== undefined) {
    const cleaned = Array.isArray(data.securityQuestions)
      ? data.securityQuestions.map((question) => (typeof question === 'string' ? question.trim() : '')).filter(Boolean)
      : [];
    payload.securityQuestions = cleaned;
  }

  if (data.failedLoginAttempts !== undefined) {
    payload.failedLoginAttempts = data.failedLoginAttempts;
  }

  if (data.lastFailedLoginAt !== undefined) {
    const value = sanitizeOptional(data.lastFailedLoginAt);
    if (value !== undefined) {
      payload.lastFailedLoginAt = value;
    }
  }

  if (data.active !== undefined) {
    const active = data.active !== false;
    payload.active = active;
  }

  return payload;
};

const ensureRequiredString = (value: string): string => (typeof value === 'string' ? value.trim() : '');

const buildCreatePayload = (formData: UserCredentialFormState): CreateUserCredentialDto => {
  const sanitized = sanitizePayload(formData);
  const active = sanitized.active ?? formData.active !== false;
  return {
    ...sanitized,
    userId: sanitized.userId ?? ensureRequiredString(formData.userId),
    cpf: sanitized.cpf ?? ensureRequiredString(formData.cpf),
    email: sanitized.email ?? ensureRequiredString(formData.email),
    requiresPasswordChange: sanitized.requiresPasswordChange ?? formData.requiresPasswordChange === true,
    active,
  };
};

class UserCredentialsService extends BaseService<
  UserCredentialRecord,
  CreateUserCredentialDto,
  UpdateUserCredentialDto,
  UserCredentialFilters
> {
  constructor() {
    super({
      basePath: '/usercredentials',
      normalizeRecord: normalizeUserCredential,
    });
  }
}

export const userCredentialsService = new UserCredentialsService();

export const userCredentialFormDefaults: UserCredentialFormState = {
  userId: '',
  cpf: '',
  email: '',
  passwordHash: '',
  passwordSalt: '',
  lastPasswordChange: '',
  passwordExpiresAt: '',
  requiresPasswordChange: false,
  socialProvider: '',
  socialProviderId: '',
  recoveryEmail: '',
  recoveryPhone: '',
  securityQuestions: [],
  failedLoginAttempts: 0,
  lastFailedLoginAt: '',
  active: true,
};

export const createUserCredentialsFormService = () =>
  new BaseFormService<UserCredentialRecord, UserCredentialFormState>({
    basePath: '/usercredentials',
    initialData: userCredentialFormDefaults,
    normalizeRecord: normalizeUserCredential,
    validationRules: {
      userId: [ValidationRules.required('User is required')],
      cpf: [ValidationRules.required('CPF is required')],
      email: [ValidationRules.required('Email is required')],
    },
    transformForCreate: (formData: UserCredentialFormState) => buildCreatePayload(formData),
    transformForUpdate: (formData: Partial<UserCredentialFormState>) => sanitizePayload(formData),
    transformToForm: (record) => {
      const resolvedActive =
        (typeof record.active === 'boolean' ? record.active : undefined) ??
        (typeof (record as any).status === 'string' ? (record as any).status !== 'inactive' : undefined) ??
        true;

      return {
        userId: record.userId ?? '',
        cpf: record.cpf ?? '',
        email: record.email ?? '',
        passwordHash: '',
        passwordSalt: '',
        lastPasswordChange: record.lastPasswordChange ?? '',
        passwordExpiresAt: record.passwordExpiresAt ?? '',
        requiresPasswordChange: record.requiresPasswordChange === true,
        socialProvider: record.socialProvider ?? '',
        socialProviderId: record.socialProviderId ?? '',
        recoveryEmail: record.recoveryEmail ?? '',
        recoveryPhone: record.recoveryPhone ?? '',
        securityQuestions: Array.isArray(record.securityQuestions) ? record.securityQuestions : [],
        failedLoginAttempts: record.failedLoginAttempts ?? 0,
        lastFailedLoginAt: record.lastFailedLoginAt ?? '',
        active: resolvedActive,
        id: record.id ? String(record.id) : '',
        recordId: record.recordId ? String(record.recordId) : record.id ? String(record.id) : '',
        createdAt: record.createdAt ?? '',
        createdBy: resolveCreatedBy(record),
        updatedAt: record.updatedAt ?? '',
        updatedBy: resolveUpdatedBy(record),
      };
    },
  });
